/**
 * Compliance Impact Section (MA-UI-107)
 * 
 * War Room section showing compliance as an economic and operational performance layer.
 * Contains: Compliance Leakage, Deployable Lead Rate, Quarantine Spike Alert, Mode Comparison
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';

// Import individual cards
import { ComplianceLeakageCard } from './ComplianceLeakageCard';
import { DeployableLeadRateCard } from './DeployableLeadRateCard';
import { QuarantineSpikeCard } from './QuarantineSpikeCard';
import { ModeComparisonCard } from './ModeComparisonCard';

// =============================================================================
// COMPLIANCE IMPACT SECTION - Parent Container
// =============================================================================

export function ComplianceImpactSection({ 
  period = 'mtd', 
  customStart,
  customEnd,
  funnelId,
  channel,
  campaignId,
  tenantId 
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['compliance-impact', period, customStart, customEnd, funnelId, channel, campaignId, tenantId],
    queryFn: async () => {
      const params = { period };
      if (customStart) params.custom_start = customStart;
      if (customEnd) params.custom_end = customEnd;
      if (funnelId) params.funnel_id = funnelId;
      if (channel && channel !== 'all') params.channel = channel;
      if (campaignId && campaignId !== 'all') params.campaign_id = campaignId;
      if (tenantId) params.tenant_id = tenantId;
      
      const response = await api.get('/analytics/compliance-impact', { params });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  // Error state
  if (error && !isLoading) {
    return (
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-slate-200">Compliance Impact</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">Unable to load compliance data. Please try again.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="compliance-impact-section">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-100">Compliance Impact</h2>
            <p className="text-sm text-slate-400">
              Measure how compliance affects deployable lead inventory, wasted spend, and operational risk.
            </p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-slate-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-sm">
                <p className="text-xs">
                  This section reframes compliance as a revenue and capital efficiency lever. 
                  Unusable leads represent wasted marketing spend.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Last updated timestamp */}
        {data?.date_range && (
          <span className="text-xs text-slate-500">
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* Row 1: Three primary KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ComplianceLeakageCard 
          data={data?.compliance_leakage}
          isLoading={isLoading}
        />
        <DeployableLeadRateCard 
          data={data?.deployable_lead_rate}
          isLoading={isLoading}
        />
        <QuarantineSpikeCard 
          data={data?.quarantine_spike}
          isLoading={isLoading}
        />
      </div>

      {/* Row 2: Mode Comparison (wider card) */}
      <ModeComparisonCard 
        captureOnly={data?.mode_comparison?.capture_only}
        enforce={data?.mode_comparison?.enforce}
        summary={data?.mode_comparison?.summary}
        isLoading={isLoading}
      />
    </div>
  );
}

export default ComplianceImpactSection;
